/**
 * 「模型管理 → 模型同步」与「系统配置 → 中台同步忽略目录 / 模型分类」共用的纯函数（无依赖，便于 node --test）。
 * 后端规则见 simulation_platform/services/model_sync/（planner / analysis）与 model_name_service.parse_categories。
 */

export const NDP_VERSION_RE = /^\d{14}$/;
export const PREVIEW_MAX_VERSIONS = 20;

export interface ParsedVersions {
  versions: string[];
  invalid: string[];
  duplicates: string[];
}

/** 多行 / 逗号 / 空白分隔的发版号输入 → 去重后的合法发版号 + 非法项 + 重复项。 */
export function parseVersionsInput(text: string): ParsedVersions {
  const tokens = text.split(/[\s,，;；]+/).map((t) => t.trim()).filter(Boolean);
  const versions: string[] = [];
  const invalid: string[] = [];
  const duplicates: string[] = [];
  for (const t of tokens) {
    if (!NDP_VERSION_RE.test(t)) {
      if (!invalid.includes(t)) invalid.push(t);
    } else if (versions.includes(t)) {
      if (!duplicates.includes(t)) duplicates.push(t);
    } else {
      versions.push(t);
    }
  }
  return { versions, invalid, duplicates };
}

export type Tone = 'success' | 'processing' | 'error' | 'warning' | 'default';

export const TASK_STATUS: Record<string, { label: string; tone: Tone }> = {
  pending: { label: '待提交', tone: 'default' },
  running: { label: '同步中', tone: 'processing' },
  succeeded: { label: '成功', tone: 'success' },
  partial_failed: { label: '部分失败', tone: 'warning' },
  failed: { label: '失败', tone: 'error' },
  cancelled: { label: '已取消', tone: 'default' },
};

export const ITEM_STATUS: Record<string, { label: string; tone: Tone }> = {
  pending: { label: '排队中', tone: 'default' },
  transferring: { label: '搬运中', tone: 'processing' },
  committing: { label: '入库中', tone: 'processing' },
  succeeded: { label: '成功', tone: 'success' },
  already_synced: { label: '无需同步', tone: 'success' },
  failed: { label: '失败', tone: 'error' },
  cancelled: { label: '已取消', tone: 'default' },
};

export const VERDICT: Record<string, { label: string; tone: Tone }> = {
  syncable: { label: '可同步', tone: 'success' },
  partial: { label: '部分可同步', tone: 'warning' },
  already_synced: { label: '无需同步', tone: 'warning' },
  blocked: { label: '不可同步', tone: 'error' },
};

export const STAGE_LABEL: Record<string, string> = {
  transfer: '搬运文件',
  commit: '入库',
  verify: '校验主版本',
};

/** 失败 / 不可同步分类码 → 人能看懂的短标签（详细原因看 message）。 */
export const REASON_LABEL: Record<string, string> = {
  INVALID_VERSION: '发版号格式',
  IN_PROGRESS: '已在同步中',
  NDP_VERSION_NOT_FOUND: '中台无此发版',
  NDP_DATA_INCONSISTENT: '中台数据不一致',
  NDP_ERROR: '读取中台失败',
  NDP_CHANGED: '中台内容已变化',
  UNKNOWN_VERSION_TYPE: '未知发版类型',
  STUB_ONNX: '空壳 onnx',
  NAME_TAKEN: '名称被占用 / 已停用',
  BASE_NOT_FOUND: '基版不存在',
  RUNTIME_INCOMPATIBLE: '仿真兼容性未通过',
  INVALID_NAME: '命名不合规',
  FETCH_FAILED: 'BOS 抓取失败',
  FETCH_TIMEOUT: '抓取超时',
  ARCHIVE_FAILED: '归档失败',
  ABORTED: '已中止',
  BOS_VERIFY_FAILED: 'BOS 校验失败',
  RELEASE_FAILED: '生成主版本失败',
  RELEASE_NAME_TAKEN: '主版本号已被占用',
  RELEASE_VERIFY_FAILED: '主版本校验失败',
  PLAN_ERROR: '数据不满足同步条件',
  NO_SYNCABLE_MODEL: '没有可同步的模型类别',
  INVALID_SKIP: '跳过的模型类别不在发版中',
  FILE_CONFLICT: '同目录文件冲突',
};

/** 基版下拉框里「不使用基版」的值：含冒号，不可能与真实主版本名（字母数字 ._-）冲突。 */
export const NO_BASE = ':none';

/** 基版下拉框的值 → 预览接口参数 {base, noBase}。空 = 后端用最新正式主版本。 */
export function baseParams(value: string | null | undefined): { base: string | null; noBase: boolean } {
  if (value === NO_BASE) return { base: null, noBase: true };
  return { base: value || null, noBase: false };
}

/** 跳过的模型类别在新主版本里的去向：有基版 → 沿用基版的版本；没有基版 → 不在新主版本里。 */
export function skipKeepText(baseRelease: string | null | undefined): string {
  return baseRelease ? '新主版本里沿用基版的版本' : '新主版本里不含该模型类别（没有基版）';
}

/** 重试时基版下拉框的值：沿用失败子项当时的基版；子项基版为空 = 当时没有用基版 → 「不使用基版」。 */
export function retryBaseValue(baseRelease: string | null | undefined): string {
  return baseRelease ? baseRelease : NO_BASE;
}

/**
 * 勾掉模型类别后，剩下要同步的都和基版一样（不变）→ 入库时判为「无需同步」、不生成主版本。
 * models 为预览摘要的模型列表；返回 true 时页面提示。原本就没有改动的（全部不变）不算。
 */
export function onlyUnchangedLeft(
  models: { name: string; skipped?: boolean; unchanged: boolean }[] | null | undefined,
  picked: string[],
): boolean {
  const syncing = (models ?? []).filter((m) => !m.skipped);
  if (!syncing.some((m) => !m.unchanged) || picked.length === 0) return false;
  const left = syncing.filter((m) => !picked.includes(m.name));
  return left.length > 0 && left.every((m) => m.unchanged);
}

/** 预览里可以发起同步的结论：全部可同步 / 部分可同步（不可同步的模型类别固定跳过）。 */
export function canSync(verdict: string | null | undefined): boolean {
  return verdict === 'syncable' || verdict === 'partial';
}

/** 文件抓取状态 → 文案与颜色。 */
export const FILE_STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: '排队', color: 'default' },
  fetching: { label: '抓取中', color: 'processing' },
  fetched: { label: '待归档', color: 'processing' },
  verified: { label: '完成', color: 'success' },
  failed: { label: '失败', color: 'error' },
};

export function reasonLabel(code: string | null | undefined): string {
  if (!code) return '';
  return REASON_LABEL[code] ?? code;
}

export function formatBytes(n: number | null | undefined): string {
  if (!n || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

export function progressPercent(progress: { done_files?: number; total_files?: number } | null | undefined): number {
  const done = progress?.done_files ?? 0;
  const total = progress?.total_files ?? 0;
  if (total <= 0) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

/* ------------------------------------------------------------------ *
 * 预览：基版版本 → 中台版本、从基版移除
 * ------------------------------------------------------------------ */

/** 模型在本次同步中的版本变化文案：不变 / 新增 / 基版版本 → 中台版本。 */
export function versionChangeText(m: {
  version: string | null; base_version: string | null; unchanged: boolean; skipped?: boolean;
}): string {
  if (m.skipped) return m.base_version ? `不同步，沿用基版 ${m.base_version}` : '不同步，新主版本不含';
  if (m.unchanged) return '不变';
  if (!m.base_version) return `新增 ${m.version}`;
  return `${m.base_version} → ${m.version}`;
}

/** 勾选状态 {发版号: [名称]} → 创建接口的 removals：只保留被选中提交的发版号、去掉空列表和预览里没列出的名称。 */
export function buildRemovals(
  picked: Record<string, string[]>,
  selectedVersions: string[],
  removableOf: (ndpVersion: string) => string[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const v of selectedVersions) {
    const allowed = new Set(removableOf(v));
    const names = [...new Set(picked[v] ?? [])].filter((n) => allowed.has(n));
    if (names.length > 0) out[v] = names;
  }
  return out;
}

/**
 * 用户勾掉的模型类别 {发版号: [名称]} → 创建接口的 skipModels：只保留被选中提交的发版号、
 * 去掉空列表和不可勾选的名称（不可同步的由后端固定跳过，不必传）。
 */
export function buildSkipModels(
  picked: Record<string, string[]>,
  selectedVersions: string[],
  syncableOf: (ndpVersion: string) => string[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const v of selectedVersions) {
    const allowed = new Set(syncableOf(v));
    const names = [...new Set(picked[v] ?? [])].filter((n) => allowed.has(n));
    if (names.length > 0) out[v] = names;
  }
  return out;
}

/** 勾选的发版号里，把可同步的模型类别全部勾掉的 → {发版号: 错误文案}（与后端一致：至少保留一个）。 */
export function skipModelErrors(
  picked: Record<string, string[]>,
  selectedVersions: string[],
  syncableOf: (ndpVersion: string) => string[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const v of selectedVersions) {
    const syncable = syncableOf(v);
    const skipped = new Set(picked[v] ?? []);
    if (syncable.length > 0 && syncable.every((n) => skipped.has(n))) out[v] = '至少保留一个要同步的模型类别';
  }
  return out;
}

/** 预览摘要里本次会同步、可以勾掉的模型类别。 */
export function syncableModels(models: { name: string; skipped?: boolean }[] | null | undefined): string[] {
  return (models ?? []).filter((m) => !m.skipped).map((m) => m.name);
}

/** 从 axios / 业务异常里取后端给的错误消息。 */
export function errorText(e: unknown, fallback = '请求失败'): string {
  const err = e as { response?: { data?: { message?: string; msg?: string } }; message?: string } | null;
  return err?.response?.data?.message || err?.response?.data?.msg || err?.message || fallback;
}

/* ------------------------------------------------------------------ *
 * 中台同步忽略目录（{"ignored": {dir: {reason, by, at}}}）
 * ------------------------------------------------------------------ */

export interface IgnoredRow { installDir: string; reason: string; by?: string; at?: string }

export function normalizeDir(dir: string): string {
  return dir.trim().replace(/^\/+|\/+$/g, '');
}

export function parseIgnored(raw: string): IgnoredRow[] {
  const data = JSON.parse(raw) as { ignored?: Record<string, { reason?: string; by?: string; at?: string }> };
  return Object.entries(data?.ignored ?? {}).map(([installDir, info]) => ({
    installDir, reason: info?.reason ?? '', by: info?.by, at: info?.at,
  }));
}

export function ignoredToJson(rows: IgnoredRow[]): string {
  const ignored: Record<string, { reason: string; by?: string; at?: string }> = {};
  for (const r of rows) {
    ignored[normalizeDir(r.installDir)] = { reason: r.reason.trim(), ...(r.by ? { by: r.by } : {}), ...(r.at ? { at: r.at } : {}) };
  }
  return JSON.stringify({ ignored }, null, 2);
}

/** 与后端同口径的前端校验；返回第一条错误，合法返回 null。 */
export function ignoredError(rows: IgnoredRow[]): string | null {
  const seen = new Set<string>();
  for (const r of rows) {
    const dir = normalizeDir(r.installDir);
    if (!dir || dir.split('/').includes('..')) return `忽略目录不合法：${r.installDir || '(空)'}`;
    if (seen.has(dir)) return `忽略目录重复：${dir}`;
    seen.add(dir);
    if (!r.reason.trim()) return `忽略目录 ${dir} 必须填写原因`;
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * 模型分类（{一级分类: {label, types: [二级分类]}}，一级分类固定）
 * ------------------------------------------------------------------ */

export const MODEL_CATEGORY_KEYS = ['perception', 'prediction', 'planning', 'control'] as const;
/** typeLabels：二级分类中文名（与中台页面上的模型类型名一致），只用于展示。 */
export interface CategoryRow { key: string; label: string; types: string[]; typeLabels: Record<string, string> }

export function parseCategories(raw: string): CategoryRow[] {
  const data = JSON.parse(raw) as Record<string, { label?: string; types?: string[]; type_labels?: Record<string, string> }>;
  return MODEL_CATEGORY_KEYS.map((key) => ({
    key, label: data?.[key]?.label ?? key, types: [...(data?.[key]?.types ?? [])],
    typeLabels: { ...(data?.[key]?.type_labels ?? {}) },
  }));
}

export function categoriesToJson(rows: CategoryRow[]): string {
  const out: Record<string, { label: string; types: string[]; type_labels?: Record<string, string> }> = {};
  for (const r of rows) {
    const types = r.types.map((t) => t.trim());
    // 只保留仍在列表里、填了中文名的二级分类（删掉二级分类时中文名一并去掉）
    const labels = Object.fromEntries(Object.entries(r.typeLabels ?? {})
      .map(([k, v]) => [k.trim(), (v ?? '').trim()] as const).filter(([k, v]) => v && types.includes(k)));
    out[r.key] = { label: r.label.trim(), types, ...(Object.keys(labels).length > 0 ? { type_labels: labels } : {}) };
  }
  return JSON.stringify(out, null, 2);
}

/** 与后端 parse_categories 同口径：二级分类非空、≤64 字符、不能跨一级分类重复。 */
export function categoriesError(rows: CategoryRow[]): string | null {
  const seen = new Map<string, string>();
  for (const r of rows) {
    if (!r.label.trim()) return `一级分类 ${r.key} 须有中文名`;
    for (const raw of r.types) {
      const t = raw.trim();
      if (!t) return `${r.label} 下有空的二级分类`;
      if (t.length > 64) return `二级分类 ${t} 超过 64 字符`;
      if (seen.has(t)) return `二级分类 ${t} 同时出现在 ${seen.get(t)} 与 ${r.label} 下`;
      seen.set(t, r.label);
      if ((r.typeLabels?.[t] ?? '').trim().length > 64) return `二级分类 ${t} 的中文名超过 64 字符`;
    }
  }
  return null;
}

/**
 * 确认同步前的主版本号校验（与后端一致）：只校验勾选了的发版号；格式为字母、数字、点、下划线、连字符，1~64 个字符，
 * 本次内不能重复。是否与已有主版本重名由后端在确认时检查。返回 {发版号: 错误文案}。
 */
export function releaseNameErrors(selected: string[], names: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  const seen = new Map<string, string>();
  for (const v of selected) {
    const n = (names[v] ?? '').trim();
    if (!/^[A-Za-z0-9._-]{1,64}$/.test(n)) {
      out[v] = n ? '只能用字母、数字、点、下划线、连字符，最长 64 个字符' : '请填写主版本号';
      continue;
    }
    const other = seen.get(n);
    if (other) {
      out[v] = `与发版号 ${other} 的主版本号重复`;
      continue;
    }
    seen.set(n, v);
  }
  return out;
}

